import LinearProgress from '@mui/material/LinearProgress';
import React, { useEffect, useRef, useState } from 'react';
import { ErrorPage } from 'components/general/ErrorPage';
import { useTranslation } from 'util/i18next';
import { KeycloakContext, ParsedJwtToken, setupKeycloak } from '@ev-tech/eva-container-api';
import { env } from '../env';

const keycloak = setupKeycloak({
  url: env.VITE_KEYCLOAK_URL,
  realm: env.VITE_KEYCLOAK_REALM,
  clientId: env.VITE_KEYCLOAK_CLIENT_ID,
});

export function KeycloakProvider({ children }: React.PropsWithChildren) {
  const authenticating = useRef(false);
  const [hasError, setHasError] = useState(false);
  const [parsedToken, setParsedToken] = useState<ParsedJwtToken>();
  const { t } = useTranslation(['user']);

  useEffect(() => {
    if (!authenticating.current) {
      authenticating.current = true;
      keycloak
        .init({ onLoad: 'login-required', checkLoginIframe: false })
        .then(() => {
          setParsedToken(keycloak.tokenParsed as ParsedJwtToken);
        })
        .catch(() => {
          setHasError(true);
        });

      keycloak.onAuthError = () => {
        setHasError(true);
      };
      keycloak.onAuthRefreshSuccess = () => {
        setParsedToken(keycloak.tokenParsed as ParsedJwtToken);
      };
    }
  }, []);

  if (hasError) {
    return (
      <ErrorPage
        title={t('user:error.authError.title')}
        message={t('user:error.authError.message')}
        buttonText={t('user:error.authError.button')}
        onClick={() => location.reload()}
      />
    );
  }

  if (!parsedToken) {
    return <LinearProgress />;
  }

  return <KeycloakContext.Provider value={parsedToken}>{children}</KeycloakContext.Provider>;
}
